"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { toast } from "react-hot-toast";
import { useAuthSession } from "@/hooks/useAuthSession";
import {
  EyeIcon,
  EyeSlashIcon,
  EnvelopeIcon,
  LockClosedIcon,
  UserIcon,
  ArrowLeftIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/react/24/outline";
import { RegisterFormData, RegisterFormErrors } from "@/types/user";
import LegalDocumentModal from "@/components/legal/LegalDocumentModal";
import Script from "next/script";
import LoadingSpinner from "@/components/common/LoadingSpinner";

const RegisterPage: React.FC = () => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [formData, setFormData] = useState<RegisterFormData>({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<RegisterFormErrors>({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalDocType, setLegalDocType] = useState<
    "terms" | "privacy" | "studentTerms" | "studentPrivacy"
  >("studentTerms");
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  // Mobile OTP registration state
  const [registerMethod, setRegisterMethod] = useState<"email" | "mobile">("mobile");
  const [mobileStep, setMobileStep] = useState<"phone" | "otp" | "name">("phone");
  const [mobilePhone, setMobilePhone] = useState("");
  const [mobileOtp, setMobileOtp] = useState("");
  const [mobileName, setMobileName] = useState("");
  const [otpSentTo, setOtpSentTo] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [mobileAcceptedTerms, setMobileAcceptedTerms] = useState(false);

  const { user, isLoading: isAuthLoading } = useAuthSession();

  // Redirect if already logged in
  useEffect(() => {
    if (!isAuthLoading && user) {
      toast.success("You're already logged in!");
      router.push("/profile");
    }
  }, [user, isAuthLoading, router]);

  // Pre-fill phone if redirected from login page
  useEffect(() => {
    const phone = searchParams.get("phone");
    if (phone) {
      setRegisterMethod("mobile");
      setMobilePhone(phone.replace(/^\+91/, "").replace(/\D/g, "").slice(0, 10));
    }
  }, [searchParams]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // Google Sign-In callback
  const handleGoogleCallback = useCallback(
    async (response: { credential: string }) => {
      setIsGoogleLoading(true);

      try {
        const res = await fetch("/api/auth/google", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            credential: response.credential,
          }),
          credentials: "include",
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || "Google sign-up failed");
        }

        // Dispatch auth event
        setTimeout(() => {
          window.dispatchEvent(new Event("auth-change"));
        }, 150);

        if (data.isNewUser) {
          toast.success("Account created with Google.");
        } else {
          toast.success("Welcome back! Signed in with Google.");
        }

        // Always redirect to profile — onboarding/fitness setup happens there
        setTimeout(() => {
          router.push(data.redirectUrl || "/profile?welcome=true");
        }, 500);
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Google sign-up failed"
        );
      } finally {
        setIsGoogleLoading(false);
      }
    },
    [router]
  );

  // Initialize Google Sign-In
  const initializeGoogleSignIn = useCallback(() => {
    if (window.google && googleButtonRef.current) {
      window.google.accounts.id.initialize({
        client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || "",
        callback: handleGoogleCallback,
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: "outline",
        size: "large",
        width: googleButtonRef.current.offsetWidth,
        text: "signup_with",
        shape: "rectangular",
        logo_alignment: "center",
      });
    }
  }, [handleGoogleCallback]);

  // Password strength checker
  const checkPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  useEffect(() => {
    setPasswordStrength(checkPasswordStrength(formData.password));
  }, [formData.password]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear specific error when user starts typing
    if (errors[name as keyof RegisterFormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = () => {
    const newErrors: RegisterFormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (!acceptedTerms) {
      newErrors.terms = "You must accept the Terms & Conditions to continue";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleMobileSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    const digits = mobilePhone.replace(/\D/g, "");
    if (!/^[6-9]\d{9}$/.test(digits)) {
      toast.error("Please enter a valid 10-digit Indian mobile number");
      return;
    }
    setOtpLoading(true);
    try {
      const res = await fetch("/api/auth/phone/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: mobilePhone, role: "STUDENT" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send OTP");
      setOtpSentTo(data.phone || mobilePhone);
      setMobileStep("otp");
      setResendCooldown(30);
      toast.success("OTP sent to your mobile number");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleMobileVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(mobileOtp)) {
      toast.error("Please enter the 6-digit OTP");
      return;
    }
    setOtpLoading(true);
    try {
      // Verify OTP but don't create account yet — just check validity
      // We re-use verify-otp in register mode after name is collected
      setMobileStep("name");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleMobileRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mobileName.trim() || mobileName.trim().length < 2) {
      toast.error("Please enter your full name (at least 2 characters)");
      return;
    }
    if (!mobileAcceptedTerms) {
      toast.error("Please accept the Terms & Conditions to continue");
      return;
    }
    setOtpLoading(true);
    try {
      const res = await fetch("/api/auth/phone/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: otpSentTo || mobilePhone,
          otp: mobileOtp,
          role: "STUDENT",
          mode: "register",
          name: mobileName.trim(),
        }),
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Registration failed");
      setTimeout(() => window.dispatchEvent(new Event("auth-change")), 150);
      toast.success("Account created! Welcome aboard 🎉");
      setTimeout(() => router.push("/profile?welcome=true"), 500);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Registration failed");
      // OTP might be wrong — go back to OTP step
      setMobileStep("otp");
      setMobileOtp("");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors below");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.toLowerCase().trim(),
          password: formData.password,
          role: "STUDENT",
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Registration failed");
      }

      // Auto-login after successful registration
      const loginResponse = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: formData.email.toLowerCase().trim(),
          password: formData.password,
        }),
      });

      if (!loginResponse.ok) {
        throw new Error("Registration successful but auto-login failed");
      }

      // Trigger auth change event after cookies are set
      setTimeout(() => {
        window.dispatchEvent(new Event("auth-change"));
      }, 150);

      toast.success("Account created! Welcome aboard 🎉");

      // Redirect to profile page — fitness onboarding happens there
      setTimeout(() => {
        router.push("/profile?welcome=true");
      }, 500);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Registration failed"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 1) return "bg-red-500";
    if (passwordStrength <= 3) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength <= 1) return "Weak";
    if (passwordStrength <= 3) return "Medium";
    return "Strong";
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">


      <div className="max-w-md w-full space-y-6 zoom-container">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mt-[80px]">
            Create Your Account
          </h2>
          <p className="mt-2 text-[#463a33]/60">
            Join our wellness community
          </p>
        </div>

        <div className="bg-white/95 rounded-lg shadow-sm sm:shadow-lg p-4 sm:p-8 border border-orange-100/50 sm:border-orange-100">
          {/* Registration method tabs */}
          <div className="mb-6 grid grid-cols-2 rounded-lg border border-gray-200 bg-gray-50 p-1">
            <button
              type="button"
              onClick={() => { setRegisterMethod("mobile"); }}
              className={`flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md transition-colors ${registerMethod === "mobile" ? "bg-white text-orange-600 shadow-sm" : "text-gray-600 hover:text-gray-900"}`}
            >
              <DevicePhoneMobileIcon className="h-4 w-4" />
              Mobile
            </button>
            <button
              type="button"
              onClick={() => { setRegisterMethod("email"); }}
              className={`flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md transition-colors ${registerMethod === "email" ? "bg-white text-orange-600 shadow-sm" : "text-gray-600 hover:text-gray-900"}`}
            >
              <EnvelopeIcon className="h-4 w-4" />
              Email
            </button>
          </div>

          {/* Mobile OTP Registration Flow */}
          {registerMethod === "mobile" && (
            <>
              {mobileStep === "phone" && (
                <form className="space-y-5" onSubmit={handleMobileSendOtp}>
                  <div>
                    <label htmlFor="mobilePhone" className="block text-sm font-medium text-[#463a33] mb-1.5">Mobile Number</label>
                    <div className="relative flex">
                      <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">+91</span>
                      <input
                        id="mobilePhone"
                        type="tel"
                        inputMode="numeric"
                        required
                        maxLength={10}
                        value={mobilePhone}
                        onChange={(e) => setMobilePhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                        className="block w-full px-3 py-3 border border-gray-300 rounded-r-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF7043] focus:border-[#FF7043]"
                        placeholder="9876543210"
                      />
                    </div>
                    <p className="mt-2 text-xs text-[#463a33]/60">We&apos;ll send a 6-digit OTP to verify your number.</p>
                  </div>
                  <button type="submit" disabled={otpLoading} className="w-full flex justify-center items-center py-3 px-4 border border-transparent text-white font-medium rounded-lg bg-[#FF7043] hover:bg-[#FF7043]/90 disabled:opacity-50 transition-all">
                    {otpLoading ? <><LoadingSpinner size="sm" showText={false} className="mr-2" /><span>Sending...</span></> : "Send OTP"}
                  </button>
                </form>
              )}

              {mobileStep === "otp" && (
                <form className="space-y-5" onSubmit={handleMobileVerifyOtp}>
                  <div>
                    <label htmlFor="mobileOtp" className="block text-sm font-medium text-[#463a33] mb-1.5">Enter OTP</label>
                    <p className="text-xs text-[#463a33]/60 mb-2">
                      Sent to {otpSentTo || `+91 ${mobilePhone}`}.{" "}
                      <button type="button" onClick={() => { setMobileStep("phone"); setMobileOtp(""); }} className="text-[#FF7043] hover:underline">Change</button>
                    </p>
                    <input
                      id="mobileOtp"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      required
                      maxLength={6}
                      value={mobileOtp}
                      onChange={(e) => setMobileOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="block w-full px-3 py-3 text-center tracking-[0.4em] text-lg border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#FF7043] focus:border-[#FF7043]"
                      placeholder="000000"
                    />
                  </div>
                  <button type="submit" disabled={otpLoading} className="w-full flex justify-center items-center py-3 px-4 border border-transparent text-white font-medium rounded-lg bg-[#FF7043] hover:bg-[#FF7043]/90 disabled:opacity-50 transition-all">
                    {otpLoading ? <><LoadingSpinner size="sm" showText={false} className="mr-2" /><span>Verifying...</span></> : "Verify OTP"}
                  </button>
                  <div className="text-center text-sm text-[#463a33]/60">
                    Didn&apos;t receive it?{" "}
                    <button type="button" disabled={resendCooldown > 0 || otpLoading} onClick={() => handleMobileSendOtp(new Event("submit") as unknown as React.FormEvent)} className="text-[#FF7043] hover:underline disabled:text-gray-400">
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend OTP"}
                    </button>
                  </div>
                </form>
              )}

              {mobileStep === "name" && (
                <form className="space-y-5" onSubmit={handleMobileRegister}>
                  <div>
                    <label htmlFor="mobileName" className="block text-sm font-medium text-[#463a33] mb-1.5">Full Name</label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#463a33]/40" />
                      <input
                        id="mobileName"
                        type="text"
                        required
                        value={mobileName}
                        onChange={(e) => setMobileName(e.target.value)}
                        className="pl-10 block w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF7043] focus:border-[#FF7043]"
                        placeholder="Enter your full name"
                      />
                    </div>
                  </div>
                  <div className="flex items-start">
                    <input
                      id="mobileTerms"
                      type="checkbox"
                      checked={mobileAcceptedTerms}
                      onChange={(e) => setMobileAcceptedTerms(e.target.checked)}
                      className="mt-1 h-4 w-4 text-[#FF7043] focus:ring-[#FF7043] border-gray-300 rounded"
                    />
                    <label htmlFor="mobileTerms" className="ml-3 text-sm text-[#463a33]/80">
                      I agree to the{" "}
                      <button type="button" onClick={() => { setLegalDocType("studentTerms"); setShowLegalModal(true); }} className="text-[#FF7043] hover:text-[#FF7043]/80 underline font-medium">Terms &amp; Conditions</button>
                      {" "}and{" "}
                      <button type="button" onClick={() => { setLegalDocType("studentPrivacy"); setShowLegalModal(true); }} className="text-[#FF7043] hover:text-[#FF7043]/80 underline font-medium">Privacy Policy</button>
                    </label>
                  </div>
                  <button type="submit" disabled={otpLoading} className="w-full flex justify-center items-center py-3 px-4 border border-transparent text-white font-medium rounded-lg bg-[#FF7043] hover:bg-[#FF7043]/90 disabled:opacity-50 transition-all">
                    {otpLoading ? <><LoadingSpinner size="sm" showText={false} className="mr-2" /><span>Creating Account...</span></> : "Create Account"}
                  </button>
                </form>
              )}
            </>
          )}

          {registerMethod === "email" && (
            <>
              <form className="space-y-5" onSubmit={handleSubmit}>
                {/* Name Field */}
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-[#463a33] mb-1.5"
                  >
                    Full Name
                  </label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#463a33]/40" />
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`pl-10 block w-full px-3 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF7043] focus:border-[#FF7043] transition-colors ${errors.name ? "border-red-300" : "border-gray-300"
                        }`}
                      placeholder="Enter your full name"
                    />
                  </div>
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                {/* Email Field */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-[#463a33] mb-1.5"
                  >
                    Email Address
                  </label>
                  <div className="relative">
                    <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#463a33]/40" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`pl-10 block w-full px-3 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF7043] focus:border-[#FF7043] transition-colors ${errors.email ? "border-red-300" : "border-gray-300"
                        }`}
                      placeholder="Enter your email"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                {/* Password Field */}
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-[#463a33] mb-1.5"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <LockClosedIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#463a33]/40" />
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      required
                      value={formData.password}
                      onChange={handleInputChange}
                      className={`pl-10 pr-10 block w-full px-3 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF7043] focus:border-[#FF7043] transition-colors ${errors.password ? "border-red-300" : "border-gray-300"
                        }`}
                      placeholder="Create a password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#463a33]/40 hover:text-[#463a33]"
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {formData.password && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-[#463a33]/60">
                          Password strength:
                        </span>
                        <span
                          className={`font-medium ${passwordStrength <= 1
                            ? "text-red-500"
                            : passwordStrength <= 3
                              ? "text-yellow-500"
                              : "text-green-500"
                            }`}
                        >
                          {getPasswordStrengthText()}
                        </span>
                      </div>
                      <div className="mt-1 w-full bg-gray-200 rounded-full h-1">
                        <div
                          className={`h-1 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                          style={{ width: `${(passwordStrength / 5) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                </div>

                {/* Confirm Password Field */}
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-[#463a33] mb-1.5"
                  >
                    Confirm Password
                  </label>
                  <div className="relative">
                    <LockClosedIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#463a33]/40" />
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className={`pl-10 pr-10 block w-full px-3 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF7043] focus:border-[#FF7043] transition-colors ${errors.confirmPassword
                        ? "border-red-300"
                        : "border-gray-300"
                        }`}
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#463a33]/40 hover:text-[#463a33]"
                    >
                      {showConfirmPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>

                {/* Terms & Conditions */}
                <div className="space-y-2">
                  <div className="flex items-start">
                    <input
                      id="acceptTerms"
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="mt-1 h-4 w-4 text-[#FF7043] focus:ring-[#FF7043] border-gray-300 rounded"
                    />
                    <label
                      htmlFor="acceptTerms"
                      className="ml-3 text-sm text-[#463a33]/80"
                    >
                      I agree to the{" "}
                      <button
                        type="button"
                        onClick={() => {
                          setLegalDocType("studentTerms");
                          setShowLegalModal(true);
                        }}
                        className="text-[#FF7043] hover:text-[#FF7043]/80 underline font-medium"
                      >
                        Terms &amp; Conditions
                      </button>{" "}
                      and{" "}
                      <button
                        type="button"
                        onClick={() => {
                          setLegalDocType("studentPrivacy");
                          setShowLegalModal(true);
                        }}
                        className="text-[#FF7043] hover:text-[#FF7043]/80 underline font-medium"
                      >
                        Privacy Policy
                      </button>
                    </label>
                  </div>
                  {errors.terms && (
                    <p className="text-sm text-red-600">{errors.terms}</p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-white font-medium rounded-lg bg-[#FF7043] hover:bg-[#FF7043]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FF7043] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Creating Account...
                    </div>
                  ) : (
                    "Create Account"
                  )}
                </button>
              </form>

            </>
          )}

          {/* Login Link */}
          <div className="mt-6 text-center">
            <span className="text-[#463a33]/60">Already have an account? </span>
            <Link
              href="/login"
              className="font-medium text-[#FF7043] hover:text-[#FF7043]/80 transition-colors"
            >
              Login Here
            </Link>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center">
          <Link
            href="/"
            className="inline-flex items-center text-[#463a33]/60 hover:text-[#463a33] transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </div>

      {/* Legal Documents Modal */}
      <LegalDocumentModal
        isOpen={showLegalModal}
        onClose={() => setShowLegalModal(false)}
        documentType={legalDocType}
      />
    </div>
  );
};

export default RegisterPage;
